import type { ReactNode } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { chartColor, type ChartToken } from "../../lib/chart-colors";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";

/** Bitta KPI kartasi. */
export type KpiItem = {
  /** Barqaror identifikator (`KpiMetrics` maydon nomi) — tilga bog'liq emas. */
  key: string;
  label: string;
  value: string;
  /** Kichik ikkinchi qator (masalan tovar miqdori yonidagi summa). */
  sub?: string;
  pct?: number | null;
  spark?: { value: number }[];
  color?: ChartToken;
  /**
   * O'sish yaxshimi? Qaytarishlar uchun `false` — aks holda qaytarishlar
   * ikki barobar oshgani yashil "muvaffaqiyat" nishoni bilan ko'rsatilardi.
   */
  higherIsBetter?: boolean;
  /** Yorliq yonidagi izoh tugmasi (masalan foyda qaytarishni ayirmasligi). */
  hint?: ReactNode;
};

export function ChangeBadge({
  pct,
  higherIsBetter = true,
}: {
  pct: number | null | undefined;
  higherIsBetter?: boolean;
}) {
  if (pct == null) return <Badge variant="outline">—</Badge>;
  const rising = pct >= 0;
  // Ikona YO'NALISHNI bildiradi (o'sish/kamayish), rang esa BAHONI — ular
  // qaytarishlar kabi metrikalarda ataylab ajraladi.
  const good = higherIsBetter ? rising : !rising;
  return (
    <Badge variant={good ? "success" : "destructive"}>
      {rising ? (
        <TrendingUp className="size-3 mr-0.5" />
      ) : (
        <TrendingDown className="size-3 mr-0.5" />
      )}
      {Math.abs(pct).toFixed(1)}%
    </Badge>
  );
}

export function KpiCard({
  id,
  label,
  value,
  sub,
  pct,
  spark,
  color = "chart-1",
  higherIsBetter,
  hint,
}: Omit<KpiItem, "key"> & { id: string }) {
  // Gradient id BARQAROR kalitdan yasaladi, tarjima qilingan yorliqdan emas:
  // ruscha yorliqlarda `[^a-zA-Z0-9]` hammasini yeb, barcha kartalar bitta
  // id'ga tushib qolardi va SVG defs global bo'lgani uchun hamma sparkline
  // bir xil gradientni olardi. (Bir vaqtda bitta KPI to'ri chiziladi — ikkita
  // bo'lsa bu id yana to'qnashardi.)
  const gradId = `spark-${id}`;
  const stroke = chartColor(color);
  return (
    <Card className={`analytics-kpi analytics-kpi--${color} h-full`}>
      <CardContent className="p-3 sm:p-4 flex flex-col gap-1.5 h-full">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1">
          <span className="truncate">{label}</span>
          {hint}
        </div>
        {/* Telefonda ustun ~160px — `text-2xl` da "12 450 000 so'm" sig'maydi.
            `truncate` EMAS: jimgina qirqilgan summa o'ralganidan battar. */}
        <div className="font-num text-lg sm:text-2xl font-bold tracking-tight tabular-nums break-words leading-tight">
          {value}
        </div>
        {sub && (
          <div className="text-xs text-muted-foreground tabular-nums">{sub}</div>
        )}
        <div className="flex items-center justify-between mt-auto pt-1 gap-2">
          <ChangeBadge pct={pct} higherIsBetter={higherIsBetter} />
          {spark && spark.length > 1 && (
            // Sparkline telefonda yashiriladi — 160px kartada u qiymatni
            // siqib qo'yardi, o'zi esa o'qilmas darajada kichik bo'lardi.
            <div className="hidden sm:block w-16 h-8 shrink-0">
              <ResponsiveContainer>
                <AreaChart data={spark} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
                  <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={stroke} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={stroke}
                    strokeWidth={1.5}
                    fill={`url(#${gradId})`}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
